import React, {MutableRefObject, useEffect} from 'react';
import {IntlShape, useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import styled from 'styled-components';

import {Icon, Text} from '@forecast-it/design-system';

import Util from '../../../shared/util/util';
import type {CopyLastWeekValidationSection_viewer$data as CopyLastWeekValidationSection_viewer} from './__generated__/CopyLastWeekValidationSection_viewer.graphql';
import {
	getBillableMinutesRegistered,
	getInvalidTimeRegistrations,
	getMinutesRegistered,
	getTimeRegistrationsByReason,
	ValidationReason,
} from './TimesheetsCopyLastWeekModalLogic';

export type ValidatedTimeRegsInDateRangeType = NonNullable<
	NonNullable<CopyLastWeekValidationSection_viewer['company']>['person']
>['doneTasksIncluded'];
export type ValidatedTimeRegRequestType = NonNullable<NonNullable<ValidatedTimeRegsInDateRangeType>[0]>['timeRegRequest'];

const ValidationContainer = styled.div`
	display: flex;
	gap: 16px;
	align-items: center;
	padding: 8px 16px 8px 12px;
	border-radius: 4px;
	background-color: #e5f4ff;
	border-left: 12px solid #0004db;
`;

const InformationContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const ValidationList = styled.ul`
	margin: 0;
	padding: 0 0 0 24px;
`;

const ValidationItem = styled.li``;

function getTimeEntriesString(timeRegistrations: ValidatedTimeRegRequestType[], intl: IntlShape): string {
	const minutesRegistered = getMinutesRegistered(timeRegistrations);
	const billableMinutesRegistered = getBillableMinutesRegistered(timeRegistrations);

	return (
		`(${Util.convertMinutesToFullHour(minutesRegistered, intl, true)}` +
		(billableMinutesRegistered > 0
			? `, ${Util.convertMinutesToFullHour(billableMinutesRegistered, intl, true)} billable)`
			: ')')
	);
}

type CopyLastWeekValidationSectionProps = {
	viewer: CopyLastWeekValidationSection_viewer;
	copyDoneTasks: boolean;
	timeRegsCount: MutableRefObject<number>;
};

const CopyLastWeekValidationSection = ({
	viewer,
	copyDoneTasks,
	timeRegsCount,
}: CopyLastWeekValidationSectionProps): JSX.Element | null => {
	const intl = useIntl();
	const timeRegistrationValidations = copyDoneTasks
		? viewer.company?.person?.doneTasksIncluded
		: viewer.company?.person?.doneTasksExcluded;
	if (!timeRegistrationValidations) return null;

	useEffect(() => {
		timeRegsCount.current = timeRegistrationValidations?.length || 0;
	}, []);

	const invalidTimeRegistrations = getInvalidTimeRegistrations(timeRegistrationValidations);

	return invalidTimeRegistrations.length > 0 ? (
		<ValidationContainer data-cy="copy-last-week-validation-section-container">
			<Icon icon={'info'} size="l" />
			<InformationContainer>
				<Text>{`${invalidTimeRegistrations.length} time registrations ${getTimeEntriesString(
					invalidTimeRegistrations,
					intl
				)} will not be copied due to company settings`}</Text>
				<ValidationList>
					{Object.entries(ValidationReason).map(([reason, descriptions]) => {
						const numberOfTimeRegs = getTimeRegistrationsByReason(timeRegistrationValidations, reason).length;

						if (numberOfTimeRegs <= 0) return null;

						const description = numberOfTimeRegs > 1 ? descriptions.plural : descriptions.singular;

						return (
							<ValidationItem key={reason}>
								<Text>
									{`${numberOfTimeRegs} ${
										numberOfTimeRegs > 1 ? 'time registrations' : 'time registration'
									} ${getTimeEntriesString(
										getTimeRegistrationsByReason(timeRegistrationValidations, reason),
										intl
									)} ${numberOfTimeRegs > 1 ? 'are' : 'is'} ${description}`}
								</Text>
							</ValidationItem>
						);
					})}
				</ValidationList>
			</InformationContainer>
		</ValidationContainer>
	) : null;
};

export const CopyLastWeekValidationSectionQuery = graphql`
	query CopyLastWeekValidationSection_Query(
		$personId: ID!
		$startYear: Int!
		$startMonth: Int!
		$startDay: Int!
		$endYear: Int!
		$endMonth: Int!
		$endDay: Int!
	) {
		viewer {
			actualPersonId
			component(name: "copy_last_week_validation_section")
			...CopyLastWeekValidationSection_viewer
				@arguments(
					personId: $personId
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
				)
		}
	}
`;

export default createFragmentContainer(CopyLastWeekValidationSection, {
	viewer: graphql`
		fragment CopyLastWeekValidationSection_viewer on Viewer
		@argumentDefinitions(
			personId: {type: "ID!"}
			startYear: {type: "Int!"}
			startMonth: {type: "Int!"}
			startDay: {type: "Int!"}
			endYear: {type: "Int!"}
			endMonth: {type: "Int!"}
			endDay: {type: "Int!"}
		) {
			company {
				person(id: $personId) {
					doneTasksExcluded: validatedTimeRegsInDateRange(
						startYear: $startYear
						startMonth: $startMonth
						startDay: $startDay
						endYear: $endYear
						endMonth: $endMonth
						endDay: $endDay
						excludeDoneTasks: true
					) {
						timeRegRequest {
							minutesRegistered
							billableMinutesRegistered
						}
						valid
						validationReasons
					}
					doneTasksIncluded: validatedTimeRegsInDateRange(
						startYear: $startYear
						startMonth: $startMonth
						startDay: $startDay
						endYear: $endYear
						endMonth: $endMonth
						endDay: $endDay
						excludeDoneTasks: false
					) {
						timeRegRequest {
							minutesRegistered
							billableMinutesRegistered
						}
						valid
						validationReasons
					}
				}
			}
		}
	`,
});
